/**
 * Inventory API Service — Formulary (Inventory Lots)
 *
 * Data-access layer for the Formulary tab of the Inventory module.
 * Calls GET /api/v1/inventory to list inventory lots for the active hospital.
 *
 * View-model mapping:
 *   ApiInventoryLotListItem  →  InventoryProduct
 *
 * Status mapping:
 *   AVAILABLE / QUARANTINED  → 'Active'
 *   EXPIRED / RECALLED       → 'Recall'
 */
import type { AxiosResponse } from 'axios';
import type { InventoryProduct, InventoryQueryParams, AddProductFormValues, StockEntry } from '../types';
import type { ProductRegistrationPayload } from '@tms-phoenix/ui-vue';
import type {
  ApiInventoryLotListItem,
  ApiInventorySummaryItem,
  ApiInventoryReceivePayload,
  ApiInventoryReceiveResponse,
  ApiInventoryUpdatePayload,
  ApiInventoryUpdateResponse,
  ApiInventoryLocation,
  ApiInventoryWithUnits,
  ApiDeleteUnitsPayload,
  ApiDeleteUnitsResponse,
  AddToCatalogPayload,
  UpdateCatalogProductPayload,
} from '../types/inventory-management-api.types';
import {
  addProductToCatalog,
  getCatalogProduct,
  removeFromCatalog,
  updateCatalogProduct,
} from './catalog-products.api';
import type { ApiCatalogProduct } from '../types/inventory-management-api.types';
import { config, API_ROUTES } from '@/core/config';
import { getApiClient } from '@/core/services/api.service';

/**
 * The complete set of API fields that may be updated for a catalog-selected
 * product (entity_type === "PRODUCT"). All other product fields are read-only
 * for these products — only the hospital-specific alias and expiration
 * overrides can be changed.
 */
export const CATALOG_PRODUCT_EDITABLE_API_FIELDS = [
  'display_name',
  'shelf_life_after_mix_refrig_hours',
  'shelf_life_after_mix_refrig_unit',
  'shelf_life_after_open_days',
  'shelf_life_after_open_unit',
] as const;

export type CatalogProductEditableField = (typeof CATALOG_PRODUCT_EDITABLE_API_FIELDS)[number];

// ── Helpers ───────────────────────────────────────────────────────────────────

const API_PREFIX = API_ROUTES.INVENTORY_MANAGEMENT;
const API_SETTINGS_PREFIX = API_ROUTES.SETTINGS;
const api = getApiClient();

/** Return the configured hospital ID or throw if it is missing. */
function requireHospitalId(): string {
  const id = config.hospitalId;
  if (!id) {
    throw new Error('[inventory.api] VITE_HOSPITAL_ID is not configured. Cannot proceed without a hospital context.');
  }
  return id;
}


// ── View-model mapper ─────────────────────────────────────────────────────────

/**
 * Map an ApiCatalogProduct (wire format) to the local InventoryProduct view-model.
 * Kept for addInventoryProduct which still uses the catalog endpoint.
 */
export function toCatalogInventoryProduct(p: ApiCatalogProduct): InventoryProduct {
  const isActive = p.is_active !== false && p.status !== 'archived' && p.status !== 'deprecated';

  return {
    id: p.product_id,
    productName: p.product_name,
    sku: p.sku ?? undefined,
    brandName: p.brand_name ?? undefined,
    localName: p.display_name ?? undefined,
    displayName: p.display_name ?? p.product_name,
    description: [p.brand_name, p.form_name].filter(Boolean).join(' · ') || undefined,
    currentStock: 0,
    maxStock: p.reorder_threshold ?? 200,
    location: '',
    status: isActive ? 'Active' : 'Recall',
    category: 'formulary',
    createdAt: p.created_at,
    updatedAt: p.updated_at,
    productId: p.product_id,
    entityType: p.entity_type ?? undefined,
  };
}

/**
 * Map an inventory summary item to the InventoryProduct view-model.
 * Used by fetchInventoryProducts for the Formulary tab list.
 */
function toSummaryInventoryProduct(item: ApiInventorySummaryItem): InventoryProduct {
  // Prefer product_name, then sku, then a readable short ID from the product UUID
  const name = item.product_name || item.sku || `Product ${item.product_id.slice(0, 8)}`;
  const raw = typeof item.total_quantity === 'string' ? Number(item.total_quantity) : item.total_quantity;
  const qty = Number.isFinite(raw) ? raw : 0;
  return {
    id: item.product_id,
    productName: name,
    sku: item.sku ?? undefined,
    brandName: item.brand_name ?? undefined,
    form: item.form_name ?? undefined,
    localName: item.display_name ?? undefined,
    displayName: item.display_name ?? name,
    description: item.brand_name || undefined,
    currentStock: qty,
    maxStock: qty > 0 ? qty : 1,
    location: '',
    status: 'Active',
    category: 'formulary',
    createdAt: '',
    updatedAt: '',
    productId: item.product_id,
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Fetch inventory lots for the active hospital via GET /api/v1/inventory/summary.
 *
 * Delegates filtering to the server where possible:
 *   - `search` — server-side case-insensitive search by product name, sku, brand_name
 */
export async function fetchInventoryProducts(
  params: InventoryQueryParams,
): Promise<{ items: InventoryProduct[]; total: number }> {
  const hospitalId = requireHospitalId();

  // Build server-side query params
  const queryParams: Record<string, string> = { hospital_id: hospitalId };

  // Delegate search to the server via `search` (matches product_name, display_name, sku, brand_name)
  if (params.search?.trim()) {
    queryParams.search = params.search.trim();
  }

  // createApiClient auto-unwraps { success, data, meta } → res.data is the inner payload
  const res: AxiosResponse<ApiInventorySummaryItem[]> =
    await api.get(`${API_PREFIX}/inventory/summary`, { params: queryParams });

  const items = res.data.map(toSummaryInventoryProduct);

  return {
    items,
    total: items.length,
  };
}

/**
 * Fetch inventory lots for a specific product via GET /api/v1/inventory
 * with both hospital_id and product_id query params.
 *
 * Used by UpdateStockPage to populate the "Current Stock" table.
 */
export async function fetchLotsByProductId(
  productId: string,
): Promise<ApiInventoryLotListItem[]> {
  const hospitalId = requireHospitalId();

  // createApiClient auto-unwraps { success, data, meta } → res.data is the inner payload
  const res: AxiosResponse<ApiInventoryLotListItem[]> =
    await api.get(`${API_PREFIX}/inventory`, {
      params: { hospital_id: hospitalId, product_id: productId },
    });

  return res.data;
}

/**
 * Update an existing inventory lot via PUT /api/v1/inventory/:inventory_id.
 *
 * Used by the Update Stock edit action. Only the changed fields need to be sent.
 */
export async function updateInventoryLot(
  inventoryId: string,
  payload: ApiInventoryUpdatePayload,
): Promise<ApiInventoryUpdateResponse> {
  const res: AxiosResponse<ApiInventoryUpdateResponse> =
    await api.put(`${API_PREFIX}/inventory/${inventoryId}`, payload);
  return res.data;
}

/**
 * Fetch units (barcodes) for a specific inventory lot via
 * GET /api/v1/inventory/:inventory_id/units.
 *
 * Called when the user clicks the Edit button on a stock row so that
 * existing barcode data (tms_uuid) is loaded into the edit form.
 *
 * Returns `{ inventory, units }` where each unit carries a `tms_uuid`
 * (the scannable barcode identifier).
 */
export async function fetchInventoryUnits(
  inventoryId: string,
): Promise<ApiInventoryWithUnits> {
  // The API client auto-unwraps { success, data, meta } → res.data is { inventory, units }
  const res: AxiosResponse<ApiInventoryWithUnits> =
    await api.get(`${API_PREFIX}/inventory/${inventoryId}/units`);
  return res.data;
}

/**
 * Dispose (soft-delete) one or more inventory units via
 * DELETE /api/v1/inventory/:inventory_id/units.
 *
 * Sets unit_status = 'DISPOSED' on the given tms_uuid barcodes so they surface
 * in the Dispose Area / disposedItems report. Unlike `updateInventoryLot`'s
 * `remove_barcodes`, this never hard-deletes the unit rows.
 */
export async function deleteInventoryUnits(
  inventoryId: string,
  tmsUuids: string[],
): Promise<ApiDeleteUnitsResponse> {
  const hospitalId = requireHospitalId();
  const payload: ApiDeleteUnitsPayload = {
    hospital_id: hospitalId,
    tms_uuids: tmsUuids,
    performed_by: config.receivedBy || config.staffId || undefined,
  };

  // Axios requires a `data` key inside the config object to send a body with DELETE.
  const res: AxiosResponse<ApiDeleteUnitsResponse> =
    await api.delete(`${API_PREFIX}/inventory/${inventoryId}/units`, { data: payload });
  return res.data;
}

/**
 * Fetch inventory storage locations via GET /settings/api/v1/inventory-locations/.
 *
 * Used by UpdateStockPage to populate the Location dropdown.
 */
export async function fetchInventoryLocations(): Promise<ApiInventoryLocation[]> {
  const hospitalId = requireHospitalId();

  const res: AxiosResponse<ApiInventoryLocation[]> =
    await api.get(`${API_SETTINGS_PREFIX}/inventory-locations/`, {
      params: { hospital_id: hospitalId },
    });

  return res.data;
}

/**
 * Delete (recall) an inventory lot via DELETE /api/v1/inventory/:inventory_id.
 *
 * The backend marks the lot and its transactions as RECALLED.
 */
export async function deleteInventoryLot(inventoryId: string): Promise<void> {
  await api.delete(`${API_PREFIX}/inventory/${inventoryId}`);
}

/**
 * Receive new inventory stock via POST /api/v1/inventory/receive.
 *
 * Sends one request per stock entry. Each entry becomes a new inventory lot
 * with auto-generated barcode units on the backend.
 *
 * @param productId  The upstream product UUID from the parent lot
 * @param fallbackLocationId Fallback location UUID when an entry has no per-row locationId
 * @param entries    Stock entries created by the user in the Update Stock form
 * @param sku       Product SKU / barcode prefix (sourced from the product record)
 * @returns Array of successfully created lot responses
 */
export async function receiveInventoryStock(
  productId: string,
  fallbackLocationId: string,
  entries: StockEntry[],
  sku?: string,
): Promise<ApiInventoryReceiveResponse[]> {
  const hospitalId = requireHospitalId();
  const results: ApiInventoryReceiveResponse[] = [];
  const errors: { index: number; entry: StockEntry; error: unknown }[] = [];

  // Resolve unit metadata from the selected product so receive payload fields
  // align with backend expectations (unit/per_unit_quantity).
  let perUnitQuantity = 1;
  let unit = '';
  try {
    const product = await getCatalogProduct(productId, hospitalId);
    const netVolumeMl = Number(product.net_volume_ml);
    if (Number.isFinite(netVolumeMl) && netVolumeMl > 0) {
      perUnitQuantity = netVolumeMl;
    }
    if (typeof product.volume_uom === 'string') {
      unit = product.volume_uom.trim();
    }
  } catch (err) {
    // Keep defaults so stock receive can proceed even if product detail lookup fails.
    console.warn('[inventory.api] Failed to resolve product unit metadata for receiveInventoryStock.', err);
  }

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    const hasExisting = entry.barcodes?.some((b) => !b.autoGenerated) ?? false;
    const barcodeValues = hasExisting
      ? entry.barcodes?.filter((b) => !b.autoGenerated).map((b) => b.barcode) ?? []
      : undefined;

    // Use per-entry locationId when available; fall back to the shared default.
    const entryLocationId = entry.locationId || fallbackLocationId;

    const payload: ApiInventoryReceivePayload = {
      hospital_id: hospitalId,
      product_id: productId,
      location_id: entryLocationId,
      expiry_date: entry.expiryDate,
      quantity: entry.quantity,
      per_unit_quantity: perUnitQuantity,
      unit,
      sku: sku || entry.lotNumber,
      batch_number: entry.lotNumber,
      lot_status: 'AVAILABLE',
      existing_2d_barcode: hasExisting,
      ...(barcodeValues ? { barcodes: barcodeValues } : {}),
    };

    try {
      // createApiClient auto-unwraps { success, data, meta } → res.data is the inner payload
      const res: AxiosResponse<ApiInventoryReceiveResponse> =
        await api.post(`${API_PREFIX}/inventory/receive`, payload);

      results.push(res.data);
    } catch (err) {
      errors.push({ index: i, entry, error: err });
    }
  }

  if (errors.length > 0) {
    const msg = `[inventory.api] receiveInventoryStock: ${errors.length}/${entries.length} entries failed. ` +
      `${results.length} succeeded.`;
    console.error(msg, errors);

    // Attach partial results to the error so the caller can reconcile
    const error = new Error(msg) as Error & {
      partialResults: ApiInventoryReceiveResponse[];
      failedEntries: typeof errors;
    };
    error.partialResults = results;
    error.failedEntries = errors;
    throw error;
  }

  return results;
}

/**
 * POST /api/v1/inventory/products/catalog
 *
 * Add a product to the hospital's formulary catalog.
 *
 * Case 1 (existing): `values.productId` supplied → master data used as base.
 * Case 2 (new):      `values.productId` omitted  → `sku` & `productName` required;
 *                    a master record is created first.
 *
 * When `values.registrationPayload` is present (full wizard submission), the
 * complete set of product fields is forwarded to the backend.
 */

/**
 * Parse a form decimal string to a number, returning `null` (not `undefined`) for
 * empty/invalid input, so clearing the field persists on PATCH — `undefined` is
 * dropped by JSON.stringify and the backend treats a missing key as "leave unchanged".
 */
function toNum(val?: string): number | null {
  if (!val || val.trim() === '') return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

/** Convert a dietary string ("true"/"yes"/"1") to boolean. Returns `null` for absent/empty values (so clearing persists on PATCH — see toNum), `true` for "true"/"yes"/"1", and `false` for any other non-empty string. */
function toBool(val?: string | boolean): boolean | null {
  if (val === undefined || val === null || val === '') return null;
  if (typeof val === 'boolean') return val;
  return val.toLowerCase() === 'true' || val.toLowerCase() === 'yes' || val === '1';
}

/**
 * Build a complete AddToCatalogPayload from the multi-step wizard payload.
 * Fields are mapped 1:1 from the form schema to the API request body.
 */
function buildFullCatalogPayload(
  reg: ProductRegistrationPayload,
  hospitalId: string,
  productId: string | undefined,
  localName?: string,
  productName?: string,
): AddToCatalogPayload {
  const bi = reg.overview.basic_information;
  const desc = reg.overview.description;
  const alg = reg.overview.allergens;
  const ver = reg.overview.verification;
  const exp = reg.overview.expiration;
  const cal = reg.nutritions.caloric_information;
  const prot = reg.nutritions.protein;
  const fat = reg.nutritions.fat;
  const carb = reg.nutritions.carbohydrates;
  const srv = reg.nutritions.serving_information;
  const ing = reg.nutritions.ingredients;
  const diet = reg.nutritions.dietary_information;
  const bvit = reg.vitamins.b_vitamins;
  const fsvit = reg.vitamins.fat_soluble_vitamins;
  const ovit = reg.vitamins.other_vitamins_related;
  const elec = reg.minerals.electrolytes;
  const maj = reg.minerals.major_minerals;
  const trace = reg.minerals.trace_minerals;
  const aad = reg.amino_acids.amino_acid_derivatives;
  const eaa = reg.amino_acids.essential_amino_acids;
  const naa = reg.amino_acids.non_essential_amino_acids;
  const pkg = reg.packaging.primary_package;
  const ctn = reg.packaging.carton_case;
  const stor = reg.packaging.storage;
  const ids = reg.other.identifiers;
  const cmpd = reg.other.other_compounds;
  const rev = reg.other.review_similar_products;

  return {
    // ── Identity ───────────────────────────────────────────────────────────
    hospital_id: hospitalId,
    ...(productId ? { product_id: productId } : {}),
    display_name: localName || undefined,
    is_preferred: false,
    is_active: true,

    // ── Basic info ─────────────────────────────────────────────────────────
    // sku/product_name/brand_name stay `|| undefined`: they're mandatory identity
    // fields, and the create schema (AddOrCreateCatalogRequestSchema) declares them
    // optional-only, not nullable — sending `null` would 400 on product creation.
    sku: bi.sku || undefined,
    product_name: productName || bi.brand_name || undefined,
    brand_name: bi.brand_name || undefined,
    manufacturer_name: bi.manufacturer || null,
    form_name: bi.form || null,
    // null (not undefined) so clearing the dropdown persists on PATCH — see toBool above.
    type_of_product: bi.type_of_product || null,
    measurement_metric: bi.measurement_metric || null,
    flavor: bi.flavor || null,
    status: bi.product_status || 'active',
    hcpcs: bi.hcpcs || null,
    ndc_format_code: bi.ndc_format_code || null,
    age_for_use: bi.age_for_use || null,
    region_for_use: bi.region_for_use || null,
    date_verified: bi.date_verified || null,
    tms_product_id: bi.tms_product_id || null,
    kcal_per_ml: toNum(bi.calories_for_liquids),
    calories_kcal: toNum(bi.calories_for_pwds),

    // ── Description ───────────────────────────────────────────────────────
    description: desc.product_description || null,
    category_name: desc.formula_classification?.length ? desc.formula_classification : null,
    item_type: desc.parenteral_category || null,
    substitutions: desc.substitutions || null,

    // ── Allergens ─────────────────────────────────────────────────────────
    contains_allergen_milk: alg.milk,
    contains_allergen_egg: alg.egg,
    contains_allergen_wheat: alg.wheat,
    contains_allergen_soy: alg.soy,
    contains_allergen_peanut: alg.peanut,
    contains_allergen_tree_nuts: alg.tree_nuts,
    contains_allergen_fish: alg.fish,
    contains_allergen_shellfish: alg.shellfish,
    contains_allergen_sesame: alg.sesame,
    contains_allergen_celery: alg.contains_allergen_celery,
    contains_allergen_mustard: alg.contains_allergen_mustard,
    contains_allergen_lupin: alg.contains_allergen_lupin,
    contains_allergen_molluscs: alg.contains_allergen_molluscs,
    contains_allergen_sulphites: alg.contains_allergen_sulphites,
    contains_allergen_lactose: alg.lactose,
    contains_allergen_latex: alg.latex,
    contains_allergen_crustaceans: alg.contains_allergen_crustaceans,
    contains_allergen_sulphur_dioxide: alg.contains_allergen_sulphur_dioxide,

    // ── Verification ──────────────────────────────────────────────────────
    verification_source: ver.verification_source_url || null,

    // ── Caloric / Nutritional ─────────────────────────────────────────────
    displacement_ml_per_g: toNum(cal.displacement_for_pwd),
    osmolality_mosm_per_kg: toNum(cal.osmolality),
    osmolarity_mosm_per_l: toNum(cal.osmolarity),
    prsl_mosm_per_100kcal: toNum(cal.prsl),
    renal_solute_load_mosml: toNum(cal.renal_solute_load),
    ml_per_100kcal: toNum(cal.volume_to_provide_100_kcal),
    water_g: toNum(cal.water),

    // ── Protein ───────────────────────────────────────────────────────────
    protein_g: toNum(prot.protein),
    protein_source: prot.protein_source || null,
    protein_hydrolysis: prot.protein_hydrolysis || null,
    bcaa_g: toNum(prot.bcaa),
    total_nitrogen_g: toNum(prot.total_n2),

    // ── Fat ───────────────────────────────────────────────────────────────
    total_fat_g: toNum(fat.fat),
    mct_g: toNum(fat.mct),
    ala_g: toNum(fat.ala),
    linoleic_omega6_mg: toNum(fat.linoleic_acid_omega_6),
    linolenic_omega3_mg: toNum(fat.linolenic_acid_omega_3),
    ara_mg: toNum(fat.ara),
    dha_mg: toNum(fat.dha),
    epa_mg: toNum(fat.epa),
    cholesterol_mg: toNum(fat.cholesterol),

    // ── Carbohydrates ─────────────────────────────────────────────────────
    cho_g: toNum(carb.cho),
    cho_source: carb.cho_source || null,
    added_sugars_g: toNum(carb.added_sugars),
    total_sugars_g: toNum(carb.total_sugars),
    dietary_fiber_g: toNum(carb.dietary_fiber),
    soluble_fiber_g: toNum(carb.soluble_fiber),
    insoluble_fiber_g: toNum(carb.insoluble_fiber),
    prebiotic_fiber_g: toNum(carb.prebiotic_fiber),
    dextrose_g: toNum(carb.dextrose),
    d_galactose_g: toNum(carb.d_galactose),

    // ── Serving ───────────────────────────────────────────────────────────
    denominator_property: srv.denominator_property || null,
    denominator_volume: toNum(srv.denominator_volume),
    recipe_dilution: srv.recipe_dilution_for_reconstitution || null,
    cup_g: toNum(srv.one_level_unpacked_cup),
    teaspoon_g: toNum(srv.one_level_unpacked_teaspoon),
    scoop_g: toNum(srv.one_unpacked_level_scoop),

    // ── Ingredients / Dietary ─────────────────────────────────────────────
    ingredients: ing.full_ingredients_list || null,
    gluten_free: toBool(diet.gluten_free),
    halal: toBool(diet.halal),
    kosher: toBool(diet.kosher),

    // ── B Vitamins ────────────────────────────────────────────────────────
    biotin_mcg: toNum(bvit.biotin),
    // "Folate (DFE mcg)" is the true DFE-folate value -> folate_dfe_mcg;
    // "Folic Acid (mcg)" is the plain mcg value -> folate_mcg. Matches the
    // shared wizard component's own labels and FDA labeling convention
    // (also matches Control Panel's mapping in product.transforms.ts).
    folate_dfe_mcg: toNum(bvit.folate),
    folate_mcg: toNum(bvit.folic_acid),
    niacin_b3_mg: toNum(bvit.niacin),
    pantothenic_acid_mg: toNum(bvit.pantothenic_acid),
    riboflavin_b2_mg: toNum(bvit.riboflavin),
    thiamin_b1_mg: toNum(bvit.thiamin),
    vitamin_b12_mcg: toNum(bvit.vitamin_b12),
    vitamin_b6_mg: toNum(bvit.vitamin_b6),

    // ── Fat-Soluble Vitamins ───────────────────────────────────────────────
    vitamin_a_mcg: toNum(fsvit.vitamin_a_rae),
    vitamin_a_iu: toNum(fsvit.vitamin_a_iu),
    vitamin_d_mcg: toNum(fsvit.vitamin_d_mcg),
    vitamin_d_iu: toNum(fsvit.vitamin_d_iu),
    vitamin_e_mg: toNum(fsvit.vitamin_e_mg),
    vitamin_e_iu: toNum(fsvit.vitamin_e_iu),
    vitamin_k_mcg: toNum(fsvit.vitamin_k),

    // ── Other Vitamins ─────────────────────────────────────────────────────
    choline_mg: toNum(ovit.choline),
    inositol_mg: toNum(ovit.inositol),
    vitamin_c_mg: toNum(ovit.vitamin_c),

    // ── Electrolytes ──────────────────────────────────────────────────────
    acetate_meq: toNum(elec.acetate),
    chloride_meq: toNum(elec.chloride_meq),
    chloride_mg: toNum(elec.chloride_mg),
    potassium_meq: toNum(elec.potassium_meq),
    potassium_mg: toNum(elec.potassium_mg),
    sodium_meq: toNum(elec.sodium_meq),
    sodium_mg: toNum(elec.sodium_mg),

    // ── Major Minerals ────────────────────────────────────────────────────
    calcium_meq: toNum(maj.calcium_meq),
    calcium_mg: toNum(maj.calcium_mg),
    magnesium_meq: toNum(maj.magnesium_meq),
    magnesium_mg: toNum(maj.magnesium_mg),
    phosphorus_mmol: toNum(maj.phosphorus_mmol),
    phosphorus_mg: toNum(maj.phosphorus_mg),

    // ── Trace Minerals ─────────────────────────────────────────────────────
    aluminum_mcg: toNum(trace.aluminum),
    chromium_mcg: toNum(trace.chromium),
    copper_mg: toNum(trace.copper),
    fluoride_mcg: toNum(trace.fluoride),
    iodine_mcg: toNum(trace.iodine),
    iron_mg: toNum(trace.iron),
    manganese_mg: toNum(trace.manganese),
    molybdenum_mcg: toNum(trace.molybdenum),
    selenium_mcg: toNum(trace.selenium),
    zinc_mg: toNum(trace.zinc),

    // ── Essential Amino Acids ─────────────────────────────────────────────
    histidine_g: toNum(eaa.histidine),
    isoleucine_g: toNum(eaa.isoleucine),
    leucine_g: toNum(eaa.leucine),
    lysine_g: toNum(eaa.lysine),
    methionine_g: toNum(eaa.methionine),
    phenylalanine_g: toNum(eaa.phenylalanine),
    threonine_g: toNum(eaa.threonine),
    tryptophan_g: toNum(eaa.tryptophan),
    valine_g: toNum(eaa.valine),

    // ── Non-Essential Amino Acids ─────────────────────────────────────────
    alanine_g: toNum(naa.alanine),
    arginine_g: toNum(naa.arginine),
    cystine_g: toNum(naa.cystine),
    glycine_g: toNum(naa.glycine),
    l_aspartic_acid_g: toNum(naa.l_aspartic_acid),
    l_glutamic_acid_g: toNum(naa.l_glutamic_acid),
    l_glutamine_g: toNum(naa.l_glutamine),
    proline_g: toNum(naa.proline),
    serine_g: toNum(naa.serine),
    tyrosine_g: toNum(naa.tyrosine),

    // ── Amino Acid Derivatives ────────────────────────────────────────────
    carnitine_mg: toNum(aad.carnitine),
    taurine_mg: toNum(aad.taurine),

    // ── Packaging — Primary ───────────────────────────────────────────────
    primary_unit_2d_barcode: pkg.two_d_barcode || null,
    primary_unit_barcode: pkg.unit_barcode || null,
    container_type: pkg.unit_form || null,
    net_volume_ml: toNum(pkg.unit_volume),
    volume_uom: pkg.volume_uom || null,

    // ── Packaging — Carton/Case ───────────────────────────────────────────
    carton_barcode: ctn.carton_barcode || null,
    case_barcode: ctn.case_barcode || null,
    primary_units_per_carton: toNum(ctn.total_primary_units_in_carton),
    primary_units_per_case: toNum(ctn.total_primary_units_in_case),
    units_per_case: toNum(ctn.carton_quantity_within_case),

    // ── Storage / Expiration ──────────────────────────────────────────────
    shelf_life_after_mix_refrig_hours: toNum(exp.expiration_after_mixing),
    shelf_life_after_mix_refrig_unit: exp.expiration_after_mixing_unit || 'hours',
    shelf_life_after_open_days: toNum(exp.expiration_after_opening),
    shelf_life_after_open_unit: exp.expiration_after_opening_unit || 'days',
    storage_opened: stor.storage_opened || null,
    storage_unopened: stor.storage_unopened || null,

    // ── Identifiers ───────────────────────────────────────────────────────
    overarching_number_ns: ids.over_arching_number_nutrition_source?.toString() || null,
    overarching_number_pens: ids.over_arching_number_pens?.toString() || null,
    overarching_number_tmnp: ids.over_arching_number_tmnp?.toString() || null,

    // ── Other Compounds ───────────────────────────────────────────────────
    lutein_mg: toNum(cmpd.lutein),
    mannitol_mg: toNum(cmpd.mannitol),
    sodium_metabisulfite_g: toNum(cmpd.sodium_metabisulfite),

    // ── Review / Similar Products ─────────────────────────────────────────
    needs_review: toBool(rev.needs_review),
    similar_products: rev.similar_products
      ? rev.similar_products.split(',').map((s) => s.trim()).filter(Boolean)
      : null,
    similarity_details: rev.similarity_details || null,
  };
}

export async function addInventoryProduct(values: AddProductFormValues): Promise<InventoryProduct> {
  const hospitalId = requireHospitalId();

  let payload: AddToCatalogPayload;

  if (values.registrationPayload) {
    // Full wizard submission — build the complete catalog body from all form steps.
    payload = buildFullCatalogPayload(
      values.registrationPayload,
      hospitalId,
      values.productId,
      values.localName,
      values.productName,
    );
  } else {
    // Build the catalog payload — always include hospital_id & display_name.
    payload = {
      hospital_id: hospitalId,
      display_name: values.localName || undefined,
      is_preferred: false,
    };

    // Case 2: new product — forward product details so the backend can create
    // the master record and catalog copy in one call.
    if (!values.productId) {
      if (!values.sku || !values.productName) {
        throw new Error('addInventoryProduct: sku and productName are required when creating a new product.');
      }
      payload.sku = values.sku;
      payload.product_name = values.productName;
      if (values.brand_name) payload.brand_name = values.brand_name;
      if (values.manufacturer) payload.manufacturer_name = values.manufacturer;
      if (values.form) payload.form_name = values.form;
      if (values.type_of_product) payload.type_of_product = values.type_of_product;
      if (values.measurement_metric) payload.measurement_metric = values.measurement_metric;
      if (values.calories_for_liquids) payload.kcal_per_ml = values.calories_for_liquids;
      if (values.calories_for_pwds) payload.calories_kcal = values.calories_for_pwds;
    }
  }

  const created = await addProductToCatalog(values.productId, payload);
  return toCatalogInventoryProduct(created);
}

/**
 * Delete a catalog product by its product_id (i.e. remove from this hospital's catalog).
 * The master product is never affected.
 */
export async function deleteInventoryProduct(id: string): Promise<boolean> {
  const hospitalId = requireHospitalId();
  await removeFromCatalog(id, hospitalId);
  return true;
}

/**
 * Update the display name (hospital-specific alias) for a catalog product.
 * Maps to PATCH /api/v1/inventory/products/:product_id/catalog with { display_name }.
 * Returns true on success — the caller patches local state for instant UI feedback.
 */
export async function updateProductLocalName(
  id: string,
  localName: string,
): Promise<boolean> {
  const hospitalId = requireHospitalId();
  await updateCatalogProduct(id, hospitalId, {
    display_name: localName || null,
  });
  return true;
}

/**
 * Update the hospital-specific Expiry After Mixing / Expiry After Opening
 * override (value + unit) for a catalog product.
 * Maps to PATCH /api/v1/inventory/products/:product_id/catalog.
 * Returns true on success.
 */
export async function updateProductExpiration(
  id: string,
  values: {
    afterMixingHours?: string;
    afterMixingUnit?: 'hours' | 'days';
    afterOpeningDays?: string;
    afterOpeningUnit?: 'hours' | 'days';
  },
): Promise<boolean> {
  const hospitalId = requireHospitalId();
  await updateCatalogProduct(id, hospitalId, {
    shelf_life_after_mix_refrig_hours: toNum(values.afterMixingHours) ?? null,
    shelf_life_after_mix_refrig_unit: values.afterMixingUnit || null,
    shelf_life_after_open_days: toNum(values.afterOpeningDays) ?? null,
    shelf_life_after_open_unit: values.afterOpeningUnit || null,
  });
  return true;
}

/**
 * Update all editable fields on a manually-added catalog product.
 * Reuses buildFullCatalogPayload for consistent field mapping, strips identity
 * fields that must not change on update, then calls PATCH.
 * Returns true on success.
 */
export async function updateProductDetails(
  id: string,
  reg: ProductRegistrationPayload & { productName?: string; localName?: string },
): Promise<boolean> {
  const hospitalId = requireHospitalId();
  const { hospital_id: _hid, product_id: _pid, is_preferred: _pref, is_active: _active, ...baseFields } =
    buildFullCatalogPayload(reg, hospitalId, undefined, reg.localName, reg.productName);
  // Explicitly null out display_name when localName is empty so existing aliases can be cleared.
  // buildFullCatalogPayload converts empty strings to undefined, which JSON.stringify omits entirely.
  const updateFields = { ...baseFields, display_name: reg.localName || null };
  await updateCatalogProduct(id, hospitalId, updateFields as UpdateCatalogProductPayload);
  return true;
}

/**
 * Fetch inventory summary rows for a given hospital.
 * GET /api/v1/inventory/summary?hospital_id=<id>
 *
 * Returns `ApiInventorySummaryItem[]` — one row per product. Callers that need a
 * unique-per-product list should deduplicate by `product_id` as a precaution.
 */
export async function fetchInventoryProductsByHospital(
  hospitalId: string,
): Promise<ApiInventorySummaryItem[]> {
  const res = await api.get<ApiInventorySummaryItem[]>(
    `${API_PREFIX}/inventory/summary`,
    { params: { hospital_id: hospitalId } },
  );
  return res.data;
}

/**
 * Fetch a single catalog product by its product_id for the active hospital.
 * Wraps getCatalogProduct with the configured hospital_id.
 * Returns null if the product is not found (404).
 */
export async function fetchCatalogProductById(
  productId: string,
): Promise<ApiCatalogProduct | null> {
  const hospitalId = requireHospitalId();
  try {
    return await getCatalogProduct(productId, hospitalId);
  } catch {
    return null;
  }
}
